import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useRouter, useSearch } from '@tanstack/react-router';
import { useNavigate } from '@utils/AvRouter';
import { SearchContext } from '../context/SearchContextProvider';

export const useCustomSearchParams = (
  { contextSearchKey = '' }: { contextSearchKey?: string; options?: any } = { contextSearchKey: '' }
) => {
  const search = useSearch({ strict: false, structuralSharing: true });
  const navigate = useNavigate();
  const { searchContext, setSearchContext } = useContext(SearchContext);
  const router = useRouter();
  const mountedPath = useMemo(() => window.location.pathname, []);
  useEffect(() => {
    if (mountedPath === window.location.pathname && !router.state.isLoading) {
      if (searchContext[contextSearchKey]) {
        navigate({ search: searchContext[contextSearchKey], replace: true, ignoreBlocker: true });
      }
    }
  }, [window.location.search]);

  const setSearchParam = useCallback(
    filter => {
      if (mountedPath === window.location.pathname) {
        if (contextSearchKey) {
          setSearchContext(prev => ({ ...prev, [contextSearchKey]: filter }));
        }
        navigate({ search: filter, replace: true, ignoreBlocker: true });
      }
    },
    [navigate]
  );

  const searchParams = useMemo(() => searchContext[contextSearchKey] || search, [search, searchContext[contextSearchKey]]);

  return useMemo<[ReturnType<typeof useSearch>, (filter: any, keepOldParams?: boolean) => void]>(
    () => [searchParams, setSearchParam],
    [searchParams]
  );
};
